import React, { useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Send, Mail, CheckCircle2, MapPin, ExternalLink, Bitcoin, Landmark, Banknote } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../contexts/CartContext";
import { TELEGRAM_HANDLE, ADMIN_EMAIL } from "../lib/api";
import { LegalDisclaimer } from "./LegalDisclaimer";

const INPOST_FINDER_URL = process.env.REACT_APP_INPOST_FINDER_URL || "https://inpost.it/trova-un-locker";

// Payment methods: key → { label, fee (decimal), helpText, icon }
const PAYMENT_METHODS = {
    crypto: {
        label: "Crypto",
        fee: 0,
        icon: Bitcoin,
        feeLabel: "0% fee",
        help: "Riceverai l'indirizzo del wallet dopo aver inviato l'ordine.",
    },
    bonifico: {
        label: "Bonifico bancario",
        fee: 0.10,
        icon: Landmark,
        feeLabel: "+10% fee",
        help: "Riceverai IBAN e intestatario in chat. Il +10% copre commissioni e tempi di accredito.",
    },
    contanti: {
        label: "Spedizione contanti",
        fee: 0,
        icon: Banknote,
        feeLabel: "0-5% fee",
        help: "Spedisci i contanti nel pacco. L'ordine parte dopo la nostra ricezione e verifica. Scegli la maggiorazione tra 0% e 5%.",
        variableFee: true,
    },
};

const buildOrderText = (form, items, subtotal, fee, grandTotal) => {
    const pm = PAYMENT_METHODS[form.payment_method] || PAYMENT_METHODS.crypto;
    const feeLabel = pm.variableFee
        ? `${form.contanti_fee_pct}% fee`
        : pm.feeLabel;
    const lines = [];
    lines.push("🛒 NUOVO ORDINE — ilveropinko.eth.limo");
    lines.push("");
    lines.push("⚠️ AVVISO: Sito puramente dimostrativo. Nessun prodotto è realmente in vendita. Contenuti a solo scopo illustrativo. Conforme alle normative vigenti.");
    lines.push("");
    lines.push(`Nome: ${form.customer_name}`);
    lines.push(`Telefono: ${form.phone}`);
    lines.push(`Corriere: ${form.shipping_method}`);
    if (form.shipping_method === "InPost") {
        lines.push(`📦 Codice Locker InPost: ${form.locker_code || "(da fornire)"}`);
    }
    lines.push(`Indirizzo: ${form.address}`);
    if (form.notes) lines.push(`Note: ${form.notes}`);
    lines.push("");
    lines.push("Prodotti:");
    for (const i of items) {
        lines.push(`• ${i.quantity}x ${i.product_name} — €${i.price.toFixed(2)}/${i.unit}`);
    }
    lines.push("");
    lines.push(`Subtotale: €${subtotal.toFixed(2)}`);
    lines.push(`💳 Pagamento: ${pm.label} (${feeLabel})`);
    if (fee > 0) lines.push(`Commissione: +€${fee.toFixed(2)}`);
    lines.push(`TOTALE: €${grandTotal.toFixed(2)}`);
    return lines.join("\n");
};

const CheckoutDialog = ({ open, onClose, onSuccess }) => {
    const { items, total, clearCart } = useCart();
    const [done, setDone] = useState(false);
    const [form, setForm] = useState({
        customer_name: "",
        phone: "",
        address: "",
        shipping_method: "BRT",
        locker_code: "",
        notes: "",
        payment_method: "crypto",
        contanti_fee_pct: 0,
    });

    const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));
    const isInPost = form.shipping_method === "InPost";
    const selectedPM = PAYMENT_METHODS[form.payment_method] || PAYMENT_METHODS.crypto;
    const effectiveFeeRate = selectedPM.variableFee ? (form.contanti_fee_pct / 100) : (selectedPM.fee || 0);
    const fee = total * effectiveFeeRate;
    const grandTotal = total + fee;

    const validate = () => {
        if (!form.customer_name || !form.phone) {
            toast.error("Compila Nome e Telefono");
            return false;
        }
        if (isInPost) {
            if (!form.locker_code) {
                toast.error("Inserisci il codice del locker InPost");
                return false;
            }
        } else {
            if (!form.address) {
                toast.error("Inserisci l'indirizzo di spedizione");
                return false;
            }
        }
        if (items.length === 0) {
            toast.error("Carrello vuoto");
            return false;
        }
        return true;
    };

    const orderText = useMemo(() => buildOrderText(form, items, total, fee, grandTotal), [form, items, total, fee, grandTotal]);

    const sendViaTelegram = (e) => {
        e.preventDefault();
        if (!validate()) return;
        const url = `https://t.me/${TELEGRAM_HANDLE}?text=${encodeURIComponent(orderText)}`;
        window.open(url, "_blank", "noopener,noreferrer");
        finalize();
    };

    const sendViaEmail = (e) => {
        e.preventDefault();
        if (!validate()) return;
        const subject = `Ordine — ${form.customer_name}`;
        const url = `mailto:${ADMIN_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(orderText)}`;
        window.location.href = url;
        finalize();
    };

    const finalize = () => {
        setDone(true);
        clearCart();
        toast.success("Ordine pronto! Conferma l'invio nell'app aperta.");
        setTimeout(() => {
            setDone(false);
            onSuccess?.();
            setForm({ customer_name: "", phone: "", address: "", shipping_method: "BRT", locker_code: "", notes: "", payment_method: "crypto", contanti_fee_pct: 0 });
        }, 2500);
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto bg-[#0a0610] border-2 border-[#7c3aed]/60 rounded-3xl text-white shadow-[0_0_32px_rgba(124,58,237,0.45)]" data-testid="checkout-dialog">
                {done ? (
                    <div className="py-10 text-center" data-testid="checkout-success">
                        <CheckCircle2 className="w-14 h-14 mx-auto text-[#e879f9] mb-4 drop-shadow-[0_0_12px_rgba(232,121,249,0.6)]" />
                        <h3 className="font-serif-display text-3xl mb-2 text-white">Ordine pronto!</h3>
                        <p className="text-white/65">Conferma l'invio nell'app che si è aperta.</p>
                    </div>
                ) : (
                    <>
                        <DialogHeader>
                            <DialogTitle className="font-serif-display text-3xl text-white">Modulo di Spedizione</DialogTitle>
                            <p className="text-sm text-white/55">
                                Nessun server tratta i tuoi dati. L'ordine va direttamente al negozio via Telegram o Email.
                            </p>
                        </DialogHeader>
                        {/* Legal disclaimer at the top of the checkout */}
                        <div className="mt-3">
                            <LegalDisclaimer variant="strong" />
                        </div>
                        <form className="space-y-4 mt-2">
                            <div>
                                <label className="font-mono text-xs uppercase tracking-widest text-white/55 mb-1 block">
                                    Nome e Cognome *
                                </label>
                                <input
                                    type="text"
                                    value={form.customer_name}
                                    onChange={(e) => update("customer_name", e.target.value)}
                                    className="w-full px-3 py-2 border border-[#7c3aed]/50 bg-[#150826]/70 rounded-xl focus:outline-none focus:border-[#e879f9] focus:shadow-[0_0_12px_rgba(232,121,249,0.3)] text-white placeholder-white/30 transition-all"
                                    required
                                    data-testid="input-customer-name"
                                />
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <label className="font-mono text-xs uppercase tracking-widest text-white/55 mb-1 block">
                                        Telefono *
                                    </label>
                                    <input
                                        type="tel"
                                        value={form.phone}
                                        onChange={(e) => update("phone", e.target.value)}
                                        className="w-full px-3 py-2 border border-[#7c3aed]/50 bg-[#150826]/70 rounded-xl focus:outline-none focus:border-[#e879f9] focus:shadow-[0_0_12px_rgba(232,121,249,0.3)] text-white placeholder-white/30 transition-all"
                                        required
                                        data-testid="input-phone"
                                    />
                                </div>
                                <div>
                                    <label className="font-mono text-xs uppercase tracking-widest text-white/55 mb-1 block">
                                        Corriere *
                                    </label>
                                    <Select value={form.shipping_method} onValueChange={(v) => update("shipping_method", v)}>
                                        <SelectTrigger className="bg-[#150826]/70 border-[#7c3aed]/50 rounded-xl text-white hover:border-[#e879f9]" data-testid="select-shipping">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent className="bg-[#0d0418] border-[#7c3aed]/50 text-white">
                                            <SelectItem value="BRT">BRT</SelectItem>
                                            <SelectItem value="UPS">UPS</SelectItem>
                                            <SelectItem value="SDA">SDA</SelectItem>
                                            <SelectItem value="InPost">📦 InPost (Locker)</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>

                            {isInPost ? (
                                <div className="space-y-2 p-3 bg-[#1a0b2e]/80 border border-[#e879f9]/40 rounded-xl" data-testid="inpost-section">
                                    <label className="font-mono text-xs uppercase tracking-widest text-[#e879f9] mb-1 block">
                                        Codice Locker InPost *
                                    </label>
                                    <input
                                        type="text"
                                        value={form.locker_code}
                                        onChange={(e) => update("locker_code", e.target.value.toUpperCase())}
                                        placeholder="es. MIL01N"
                                        className="w-full px-3 py-2 border border-[#7c3aed]/50 bg-[#0d0418] rounded-xl focus:outline-none focus:border-[#e879f9] text-white font-mono placeholder-white/30"
                                        required
                                        data-testid="input-locker-code"
                                    />
                                    <a
                                        href={INPOST_FINDER_URL}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="inline-flex items-center gap-2 text-sm text-[#e879f9] hover:text-[#f0abfc] font-medium"
                                        data-testid="inpost-finder-link"
                                    >
                                        <MapPin className="w-4 h-4" />
                                        Trova il locker più vicino
                                        <ExternalLink className="w-3 h-3" />
                                    </a>
                                </div>
                            ) : null}

                            <div>
                                <label className="font-mono text-xs uppercase tracking-widest text-white/55 mb-1 block">
                                    {isInPost ? "Indirizzo di riserva (opzionale)" : "Indirizzo Completo *"}
                                </label>
                                <input
                                    type="text"
                                    value={form.address}
                                    onChange={(e) => update("address", e.target.value)}
                                    placeholder="Via, n., CAP, Città, Provincia"
                                    className="w-full px-3 py-2 border border-[#7c3aed]/50 bg-[#150826]/70 rounded-xl focus:outline-none focus:border-[#e879f9] focus:shadow-[0_0_12px_rgba(232,121,249,0.3)] text-white placeholder-white/30 transition-all"
                                    required={!isInPost}
                                    data-testid="input-address"
                                />
                            </div>
                            <div>
                                <label className="font-mono text-xs uppercase tracking-widest text-white/55 mb-1 block">
                                    Note (opzionale)
                                </label>
                                <textarea
                                    value={form.notes}
                                    onChange={(e) => update("notes", e.target.value)}
                                    rows={2}
                                    className="w-full px-3 py-2 border border-[#7c3aed]/50 bg-[#150826]/70 rounded-xl focus:outline-none focus:border-[#e879f9] focus:shadow-[0_0_12px_rgba(232,121,249,0.3)] resize-none text-white placeholder-white/30 transition-all"
                                    data-testid="input-notes"
                                />
                            </div>

                            {/* Metodo di pagamento */}
                            <div data-testid="payment-section">
                                <label className="font-mono text-xs uppercase tracking-widest text-white/55 mb-1 block">
                                    Metodo di pagamento *
                                </label>
                                <div className="grid grid-cols-3 gap-2">
                                    {Object.entries(PAYMENT_METHODS).map(([key, pm]) => {
                                        const Icon = pm.icon;
                                        const active = form.payment_method === key;
                                        return (
                                            <button
                                                key={key}
                                                type="button"
                                                onClick={() => update("payment_method", key)}
                                                data-testid={`payment-${key}`}
                                                aria-pressed={active}
                                                className={
                                                    "flex flex-col items-center gap-1 px-2 py-2.5 rounded-xl border active:scale-[0.97] transition-all " +
                                                    (active
                                                        ? "border-[#e879f9] bg-gradient-to-br from-[#e879f9]/15 to-[#7c3aed]/15 shadow-[0_0_12px_rgba(232,121,249,0.4)]"
                                                        : "border-[#7c3aed]/40 bg-[#150826]/60 hover:border-[#7c3aed] hover:bg-[#1f0f36]")
                                                }
                                            >
                                                <Icon className={`w-4 h-4 ${active ? "text-[#e879f9]" : "text-white/70"}`} />
                                                <span className={`font-mono text-[10px] uppercase tracking-wider font-bold leading-tight text-center ${active ? "text-[#e879f9]" : "text-white"}`}>
                                                    {pm.label}
                                                </span>
                                                <span className={`font-mono text-[9px] tabular-nums ${(pm.variableFee && form.contanti_fee_pct > 0) || pm.fee > 0 ? "text-amber-300" : "text-emerald-400"}`}>
                                                    {pm.variableFee && active ? `${form.contanti_fee_pct}% fee` : pm.feeLabel}
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>
                                <p className="mt-2 text-[11px] text-white/55 leading-relaxed border-l-2 border-[#7c3aed]/40 pl-2.5">
                                    {selectedPM.help}
                                </p>

                                {/* Variable fee slider — visible only for "contanti" */}
                                {selectedPM.variableFee && (
                                    <div className="mt-3 p-3 rounded-2xl border border-[#7c3aed]/40 bg-[#150826]/60" data-testid="contanti-fee-slider">
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="font-mono text-[10px] uppercase tracking-widest text-white/60">
                                                Maggiorazione
                                            </span>
                                            <span className="font-mono text-sm font-bold text-[#e879f9] tabular-nums" data-testid="contanti-fee-value">
                                                {form.contanti_fee_pct}%
                                            </span>
                                        </div>
                                        <input
                                            type="range"
                                            min="0"
                                            max="5"
                                            step="1"
                                            value={form.contanti_fee_pct}
                                            onChange={(e) => update("contanti_fee_pct", parseInt(e.target.value, 10) || 0)}
                                            data-testid="contanti-fee-range"
                                            className="w-full h-2 rounded-full appearance-none bg-[#0d0418] cursor-pointer accent-[#e879f9] [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[#e879f9] [&::-webkit-slider-thumb]:shadow-[0_0_10px_rgba(232,121,249,0.7)] [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-[#e879f9] [&::-moz-range-thumb]:border-0"
                                        />
                                        <div className="flex justify-between mt-1.5 font-mono text-[9px] text-white/35 tabular-nums">
                                            <span>0%</span>
                                            <span>1%</span>
                                            <span>2%</span>
                                            <span>3%</span>
                                            <span>4%</span>
                                            <span>5%</span>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="flex flex-col gap-3 pt-3 border-t border-[#7c3aed]/30">
                                <div className="font-mono text-sm space-y-1">
                                    <div className="flex items-center justify-between text-white/55 text-xs">
                                        <span className="uppercase tracking-widest">Subtotale</span>
                                        <span className="tabular-nums">€{total.toFixed(2)}</span>
                                    </div>
                                    {fee > 0 && (
                                        <div className="flex items-center justify-between text-amber-300 text-xs" data-testid="checkout-fee-row">
                                            <span className="uppercase tracking-widest">
                                                Commissione {selectedPM.label} ({selectedPM.variableFee ? `${form.contanti_fee_pct}%` : `${Math.round(selectedPM.fee * 100)}%`})
                                            </span>
                                            <span className="tabular-nums">+€{fee.toFixed(2)}</span>
                                        </div>
                                    )}
                                    <div className="flex items-center justify-between pt-2 mt-1 border-t border-[#7c3aed]/20">
                                        <span className="text-white/70 uppercase tracking-widest text-xs">Totale da pagare</span>
                                        <span className="text-[#e879f9] font-bold text-xl drop-shadow-[0_0_8px_rgba(232,121,249,0.4)] tabular-nums" data-testid="checkout-grand-total">
                                            €{grandTotal.toFixed(2)}
                                        </span>
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                    <button
                                        type="button"
                                        onClick={sendViaTelegram}
                                        className="inline-flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-[#7c3aed] to-[#9333ea] hover:from-[#a78bfa] hover:to-[#e879f9] text-white font-mono uppercase tracking-[0.15em] text-xs font-bold rounded-full shadow-[0_4px_14px_rgba(124,58,237,0.5)] hover:shadow-[0_6px_22px_rgba(232,121,249,0.7)] active:scale-[0.98] transition-all"
                                        data-testid="submit-telegram-btn"
                                    >
                                        <Send className="w-4 h-4" />
                                        Invia su Telegram
                                    </button>
                                    <button
                                        type="button"
                                        onClick={sendViaEmail}
                                        className="inline-flex items-center justify-center gap-2 px-4 py-3 bg-[#150826] border border-[#7c3aed]/50 hover:border-[#e879f9] text-[#e879f9] font-mono uppercase tracking-[0.15em] text-xs font-bold rounded-full active:scale-[0.98] transition-all"
                                        data-testid="submit-email-btn"
                                    >
                                        <Mail className="w-4 h-4" />
                                        Invia via Email
                                    </button>
                                </div>
                                <p className="text-[10px] font-mono text-white/40 text-center">
                                    Nessun dato passa per un server centrale.
                                </p>
                            </div>
                        </form>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default CheckoutDialog;
